import React, { Children, forwardRef, ReactNode, Ref, useEffect, useMemo, useState } from 'react';

import Flex, { FlexProps } from '@gojob/ui-bits/Flex';
import Dots from './Dots';
import { DotProps } from './Dots/Dot';
import IntersectionTracker from './IntersectionTracker';

export type DotsRenderer = (index: number, props: DotProps) => ReactNode;

export interface ScrollerProps extends Omit<FlexProps<'div'>, 'ref'> {
  showDots?: boolean;
  renderDotAriaLabel?: (index: number) => string;
  ContainerProps?: FlexProps<'div'>;
  ItemProps?: FlexProps<'div'>;
  itemPadding?: FlexProps<'div'>['padding'];
  children?: ReactNode;
}

const useStyles = () => {
  return useMemo(() => {
    return {
      container: {
        maxWidth: '100%',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        touchAction: 'pax-x',
      },
      item: {
        flex: '1 1 auto',
        scrollSnapStop: 'always',
        scrollSnapAlign: 'center',
      },
    } as const;
  }, []);
};

const Scroller = (props: ScrollerProps, ref: Ref<HTMLDivElement>) => {
  const {
    children,
    itemPadding = 1,
    ItemProps,
    ContainerProps,
    showDots,
    renderDotAriaLabel,
    ...other
  } = props;
  const classes = useStyles();

  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [visibilities, setVisibilities] = useState<number[]>([]);

  /** Reset visibilities when children changes */
  useEffect(() => {
    const childrenCount = Children.count(children);
    setVisibilities(Array.apply(null, Array(childrenCount)).map(() => 0));
  }, [children]);

  const handleOnBulletClick = (index: number) => setFocusedIndex(index);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  useEffect(() => {
    /** As soon as the focused index flushes, we want to reset it */
    if (focusedIndex >= 0) {
      setFocusedIndex(-1);
    }
  }, [focusedIndex]);

  return (
    <Flex ref={ref} maxWidth="100%" flexDirection="column" alignItems="center" {...other}>
      <Flex
        ref={setContainer}
        data-testid="ScrollerContainer"
        css={classes.container}
        flexDirection="row"
        alignItems="stretch"
        {...ContainerProps}
      >
        {Children.map(children, (child, index) => {
          const onIntersection = (visibility?: number) => {
            setVisibilities((v) => Object.assign([], v, { [index]: visibility }));
          };
          return (
            <Flex key={index} css={classes.item} padding={itemPadding} {...ItemProps}>
              <IntersectionTracker
                root={container}
                focus={focusedIndex === index}
                onIntersection={onIntersection}
              >
                {child}
              </IntersectionTracker>
            </Flex>
          );
        })}
      </Flex>
      {showDots && (
        <Dots
          visibilities={visibilities}
          onClick={handleOnBulletClick}
          renderAriaLabel={renderDotAriaLabel}
        />
      )}
    </Flex>
  );
};

export default forwardRef(Scroller);
